import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {PageTitleComponent} from "../../../components/page-title/page-title.component";
import {SubmitButtonComponent} from "../../../components/submit-button/submit-button.component";
import {UserService} from "../../../services/user.service";
import {PaymentService} from "../../../services/payment/payment.service";
import {PageSubtitleLayoutComponent} from "../../../components/page-subtitle-layout/page-subtitle-layout.component";
import {finalize, of, tap} from "rxjs";
import {Dialog} from "primeng/dialog";
import {NotificationService} from "../../../services/notification/notification.service";
import {
  MessageDialogContentComponent
} from "../../../services/notification/message-dialog-content/message-dialog-content.component";
import {CurrencyPipe, DatePipe} from "@angular/common";
import {FormButtonsComponent} from "../../../components/form-buttons/form-buttons.component";
import {AnalyticsService} from "../../../services/analytics.service";
import {PurchasesPackage} from "@revenuecat/purchases-capacitor";
import {isNative} from "../../../utils";
import {Button} from "primeng/button";
import {RadioButton} from "primeng/radiobutton";
import {CancelReason, cancelReasonOptions} from "homey-housing-lottery-app-dto";
import {Textarea} from "primeng/textarea";
import {NonNullableFormBuilder, ReactiveFormsModule, Validators} from "@angular/forms";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {Router} from "@angular/router";
import {FloorPipe} from "../../../../floor.pipe";

@Component({
  selector: 'homey-account-subscription-page',
  imports: [
    PageTitleComponent,
    SubmitButtonComponent,
    PageSubtitleLayoutComponent,
    FormButtonsComponent,
    DatePipe,
    Dialog,
    Button,
    RadioButton,
    Textarea,
    ReactiveFormsModule,
    CurrencyPipe,
    FloorPipe,
  ],
  templateUrl: './account-subscription-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountSubscriptionPageComponent {
  private readonly paymentService = inject(PaymentService);
  private readonly userService = inject(UserService);
  private readonly notificationService = inject(NotificationService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly router = inject(Router)

  protected readonly ohWereSorry = `🧡 Oh, we're sorry...`
  protected readonly reasonOptions = cancelReasonOptions;
  protected readonly reasonLabels: Record<CancelReason, string> = {
    "no-suitable-apartments": "I didn't find suitable apartments",
    "too-expensive": "Homey is too expensive",
    bug: "I encountered technical issues",
    other: "Other"
  }

  protected readonly cancelForm = this.fb.group({
    reason: this.fb.control<CancelReason | undefined>(undefined, {validators: [Validators.required]}),
    details: this.fb.control<string>('', {validators: [Validators.required, Validators.minLength(3)]}),
  })

  constructor() {
    this.cancelForm.valueChanges.pipe(
      takeUntilDestroyed()
    ).subscribe(
      value => {
        if ((value.reason === 'too-expensive' || value.reason === 'bug' || value.reason === 'other') && this.canShowDiscountPopup()) {
          this.analyticsService.sendEvent('stay-homey-discount-opened', {
            reason: value.reason
          });
          this.subscriptionDiscountDialogOpen.set(true);
          this.discountPopupShown.set(true);
        }
      }
    )
  }


  protected onCancelClick() {
    this.cancelOpen.set(true);
  }

  protected readonly canShowDiscountPopup = computed(
    () => {
      const user = this.userService.userProperties();
      if (!user) return false;
      if (user.extendedTrial) return false;
      if (user.extendedSubscription) return false;
      if (user.appliedStayDiscount) return false;
      if (this.discountPopupShown()) return false;
      const paymentInfo = this.paymentInfo();
      if (!paymentInfo) return false;
      if (paymentInfo.type !== 'stripe') return false;

      return paymentInfo.mainSubscriptionPrice === paymentInfo.currentSubscriptionPrice;
    }
  );

  protected readonly submit = async () => {
    return await this.paymentService.createCustomerPortalSession("details");
  }

  protected readonly discountPopupShown = signal(false);
  protected readonly user = this.userService.userAfterSignupFlow;
  protected readonly paymentInfo = computed(
    () => {
      const user = this.user();
      if (!user) return undefined;
      if (user.paymentInfo.type !== 'stripe') return undefined;
      return user.paymentInfo;
    }
  )

  protected readonly cancelOpen = signal(false);

  protected readonly submitDiscount = (value: 'cancel' | 'discount') => {
    if (value === 'discount') {
      this.analyticsService.sendEvent('stay-homey-discount-applied', {
        reason: this.cancelForm.value.reason ?? 'unknown'
      });
    } else {
      this.analyticsService.sendEvent('stay-homey-discount-ignored', {
        reason: this.cancelForm.value.reason ?? 'unknown'
      });
    }

    if (value === 'discount') {
      return this.paymentService.discountStayOnHomeySubscription().pipe(
        tap(() => {
          this.notificationService.openNotificationDialog([
              {
                component: MessageDialogContentComponent, data: {
                  title: `You're all set!`,
                  message: `Your subscription has been updated — you'll be saving 50% on Homey, forever.`
                }
              },
            ]
          )

          this.router.navigateByUrl('/');
        }),
        finalize(
          () => this.subscriptionDiscountDialogOpen.set(false)
        )
      )
    } else {
      this.subscriptionDiscountDialogOpen.set(false);
      this.cancelOpen.set(true);
      return of(undefined);
    }
  }

  protected readonly submitCancelSubscription = (value: 'confirm' | 'back') => {
    if (value === 'back') {
      this.cancelOpen.set(false);
      return of(undefined);
    } else {
      return this.userService.cancelSubscription(
        {
          reason: this.cancelForm.getRawValue().reason!,
          details: this.cancelForm.getRawValue().details
        }
      ).pipe(
        tap(() => {
          this.cancelOpen.set(false);
          this.notificationService.openNotificationDialog([
              {
                component: MessageDialogContentComponent, data: {
                  title: 'Your subscription has been canceled',
                  message: `We're sad to see you go, but you can always come back if you want to submit additional applications.`
                }
              },
            ]
          )
        }),
        finalize(
          () => this.cancelOpen.set(false)
        )
      )
    }
  }

  protected readonly subscriptionDiscountDialogOpen = signal(false);
  protected readonly options = signal<PurchasesPackage[]>([]);

  protected readonly isNative = isNative;
  protected readonly cancelReasonOptions = cancelReasonOptions;
}

